<template>
  <div class="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
    <!-- Header -->
    <header class="relative">
      <!-- Navigation -->
      <nav class="absolute top-0 left-0 right-0 z-50">
        <div class="container mx-auto px-6 py-4">
          <div class="flex items-center justify-between">
            <!-- Logo -->
            <router-link to="/" class="flex items-center space-x-3 group">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-12 h-12 rounded-2xl shadow-lg shadow-emerald-500/25" />
              <span class="text-2xl font-bold text-white">OilBuddyAI</span>
            </router-link>

            <!-- Navigation Links -->
            <div class="hidden md:flex items-center space-x-8">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <router-link to="/roadmap" class="text-emerald-400 font-medium">Roadmap</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>

            <!-- Mobile Menu Button -->
            <button @click="toggleMobileMenu" class="md:hidden text-white">
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16"/>
              </svg>
            </button>
          </div>

          <!-- Mobile Menu -->
          <div v-if="showMobileMenu" class="md:hidden mt-4 py-4 border-t border-slate-700">
            <div class="flex flex-col space-y-4">
              <router-link to="/" class="text-slate-300 hover:text-white transition-colors duration-200">Home</router-link>
              <router-link to="/blog" class="text-slate-300 hover:text-white transition-colors duration-200">Blog</router-link>
              <router-link to="/support" class="text-slate-300 hover:text-white transition-colors duration-200">Support</router-link>
              <router-link to="/contact" class="text-slate-300 hover:text-white transition-colors duration-200">Contact</router-link>
              <router-link to="/roadmap" class="text-emerald-400 font-medium">Roadmap</router-link>
              <a href="/#/login" class="btn-outline">Sign In</a>
              <router-link to="/register" class="btn-primary">Get Started</router-link>
            </div>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content -->
    <main class="pt-24 pb-16">
      <div class="container mx-auto px-6">
        <!-- Page Header -->
        <div class="text-center mb-16">
          <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
            <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
            Development Roadmap
          </div>
          <h1 class="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            Our Product Roadmap
          </h1>
          <p class="text-xl text-slate-300 max-w-3xl mx-auto">
            See what we're building next and track our progress as we revolutionize the oil and gas industry with AI-powered solutions.
          </p>
        </div>

        <!-- Roadmap Stats -->
        <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-16">
          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-white mb-2">{{ completedFeaturesCount }}</h3>
            <p class="text-slate-400">Features Completed</p>
          </div>

          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-white mb-2">{{ inProgressFeaturesCount }}</h3>
            <p class="text-slate-400">In Progress</p>
          </div>

          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-white mb-2">{{ plannedFeaturesCount }}</h3>
            <p class="text-slate-400">Planned</p>
          </div>

          <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 text-center">
            <div class="w-12 h-12 bg-purple-500/20 rounded-xl flex items-center justify-center mx-auto mb-4">
              <svg class="w-6 h-6 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/>
              </svg>
            </div>
            <h3 class="text-2xl font-bold text-white mb-2">{{ futureFeaturesCount }}</h3>
            <p class="text-slate-400">Future Ideas</p>
          </div>
        </div>

        <!-- Kanban Board -->
        <div class="mb-16">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">Development Progress</h2>
          <div class="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <!-- Completed -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-emerald-500/30 rounded-2xl p-6">
                             <div class="flex items-center justify-between mb-6">
                 <h3 class="text-xl font-semibold text-emerald-400">✅ Completed</h3>
                 <span class="bg-emerald-500/20 text-emerald-400 text-sm px-3 py-1 rounded-full">{{ completedFeaturesCount }}</span>
               </div>
              <div class="space-y-4">
                <div 
                  v-for="feature in completedFeatures" 
                  :key="feature.id"
                  class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4 hover:border-emerald-500/50 transition-colors duration-200"
                >
                  <h4 class="text-white font-medium mb-2">{{ feature.title }}</h4>
                  <p class="text-slate-400 text-sm mb-3">{{ feature.description }}</p>
                  <div class="flex items-center justify-between">
                    <span class="text-emerald-400 text-xs font-medium">{{ feature.category }}</span>
                    <span class="text-slate-500 text-xs">{{ feature.completedDate }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- In Progress -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-blue-500/30 rounded-2xl p-6">
                             <div class="flex items-center justify-between mb-6">
                 <h3 class="text-xl font-semibold text-blue-400">🔄 In Progress</h3>
                 <span class="bg-blue-500/20 text-blue-400 text-sm px-3 py-1 rounded-full">{{ inProgressFeaturesCount }}</span>
               </div>
              <div class="space-y-4">
                <div 
                  v-for="feature in inProgressFeatures" 
                  :key="feature.id"
                  class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4 hover:border-blue-500/50 transition-colors duration-200"
                >
                  <h4 class="text-white font-medium mb-2">{{ feature.title }}</h4>
                  <p class="text-slate-400 text-sm mb-3">{{ feature.description }}</p>
                  <div class="mb-3">
                    <div class="flex items-center justify-between text-xs text-slate-500 mb-1">
                      <span>Progress</span>
                      <span>{{ feature.progress }}%</span>
                    </div>
                    <div class="w-full bg-slate-600 rounded-full h-2">
                      <div 
                        class="bg-blue-500 h-2 rounded-full transition-all duration-300"
                        :style="{ width: feature.progress + '%' }"
                      ></div>
                    </div>
                  </div>
                  <div class="flex items-center justify-between">
                    <span class="text-blue-400 text-xs font-medium">{{ feature.category }}</span>
                    <span class="text-slate-500 text-xs">{{ feature.estimatedCompletion }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Planned -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-orange-500/30 rounded-2xl p-6">
              <div class="flex items-center justify-between mb-6">
                <h3 class="text-xl font-semibold text-orange-400">📋 Planned</h3>
                <span class="bg-orange-500/20 text-orange-400 text-sm px-3 py-1 rounded-full">{{ plannedFeaturesCount }}</span>
              </div>
              <div class="space-y-4">
                <div 
                  v-for="feature in plannedFeatures" 
                  :key="feature.id"
                  class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4 hover:border-orange-500/50 transition-colors duration-200"
                >
                  <h4 class="text-white font-medium mb-2">{{ feature.title }}</h4>
                  <p class="text-slate-400 text-sm mb-3">{{ feature.description }}</p>
                  <div class="flex items-center justify-between">
                    <span class="text-orange-400 text-xs font-medium">{{ feature.category }}</span>
                    <span class="text-slate-500 text-xs">{{ feature.plannedDate }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Future Ideas -->
            <div class="bg-slate-800/50 backdrop-blur-sm border border-purple-500/30 rounded-2xl p-6">
              <div class="flex items-center justify-between mb-6">
                <h3 class="text-xl font-semibold text-purple-400">💡 Future Ideas</h3>
                <span class="bg-purple-500/20 text-purple-400 text-sm px-3 py-1 rounded-full">{{ futureFeaturesCount }}</span>
              </div>
              <div class="space-y-4">
                <div 
                  v-for="feature in futureFeatures" 
                  :key="feature.id"
                  class="bg-slate-700/50 border border-slate-600/50 rounded-lg p-4 hover:border-purple-500/50 transition-colors duration-200"
                >
                  <h4 class="text-white font-medium mb-2">{{ feature.title }}</h4>
                  <p class="text-slate-400 text-sm mb-3">{{ feature.description }}</p>
                  <div class="flex items-center justify-between">
                    <span class="text-purple-400 text-xs font-medium">{{ feature.category }}</span>
                    <span class="text-slate-500 text-xs">{{ feature.priority }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Timeline Section -->
        <div class="mb-16">
          <h2 class="text-3xl font-bold text-white mb-8 text-center">Development Timeline</h2>
          <div class="relative">
            <!-- Timeline Line -->
            <div class="absolute left-1/2 transform -translate-x-1/2 w-1 bg-slate-700 h-full"></div>
            
            <div class="space-y-12">
              <div 
                v-for="(milestone, index) in timeline" 
                :key="milestone.id"
                class="relative flex items-center"
                :class="index % 2 === 0 ? 'flex-row' : 'flex-row-reverse'"
              >
                <!-- Timeline Dot -->
                <div class="absolute left-1/2 transform -translate-x-1/2 w-4 h-4 rounded-full border-4 border-slate-700 bg-emerald-500 z-10"></div>
                
                <!-- Content -->
                <div 
                  class="w-5/12 p-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl"
                  :class="index % 2 === 0 ? 'mr-auto' : 'ml-auto'"
                >
                  <div class="flex items-center justify-between mb-4">
                    <h3 class="text-xl font-semibold text-white">{{ milestone.title }}</h3>
                    <span class="text-emerald-400 text-sm font-medium">{{ milestone.date }}</span>
                  </div>
                  <p class="text-slate-300 mb-4">{{ milestone.description }}</p>
                  <div class="flex flex-wrap gap-2">
                    <span 
                      v-for="tag in milestone.tags" 
                      :key="tag"
                      class="px-3 py-1 bg-slate-700/50 text-slate-300 text-xs rounded-full"
                    >
                      {{ tag }}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Feature Request Section -->
        <div class="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-8">
          <h2 class="text-3xl font-bold text-white mb-6 text-center">Have a Feature Request?</h2>
          <p class="text-slate-300 text-center mb-8 max-w-2xl mx-auto">
            We value your input! Let us know what features you'd like to see in OilBuddyAI. Your suggestions help shape our product roadmap.
          </p>
          <form @submit.prevent="submitFeatureRequest" class="max-w-2xl mx-auto">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Name</label>
                <input 
                  v-model="featureRequest.name"
                  type="text" 
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                  placeholder="Your name"
                >
              </div>
              <div>
                <label class="block text-sm font-medium text-slate-300 mb-2">Email</label>
                <input 
                  v-model="featureRequest.email"
                  type="email" 
                  required
                  class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                  placeholder="your.email@example.com"
                >
              </div>
            </div>
            <div class="mb-6">
              <label class="block text-sm font-medium text-slate-300 mb-2">Feature Title</label>
              <input 
                v-model="featureRequest.title"
                type="text" 
                required
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                placeholder="Brief title for your feature request"
              >
            </div>
            <div class="mb-6">
              <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
              <textarea 
                v-model="featureRequest.description"
                rows="4"
                required
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 resize-none"
                placeholder="Describe your feature request in detail..."
              ></textarea>
            </div>
            <div class="mb-6">
              <label class="block text-sm font-medium text-slate-300 mb-2">Category</label>
              <select 
                v-model="featureRequest.category"
                required
                class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50"
              >
                <option value="">Select a category</option>
                <option value="ai-features">AI Features</option>
                <option value="job-management">Job Management</option>
                <option value="financial-tools">Financial Tools</option>
                <option value="safety-compliance">Safety & Compliance</option>
                <option value="mobile-app">Mobile App</option>
                <option value="integrations">Integrations</option>
                <option value="ui-ux">UI/UX Improvements</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div class="text-center">
              <button 
                type="submit" 
                :disabled="isSubmitting"
                class="btn-primary-modern disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg v-if="isSubmitting" class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {{ isSubmitting ? 'Submitting...' : 'Submit Feature Request' }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </main>

    <!-- Footer -->
    <footer class="bg-slate-900/50 border-t border-slate-700/50">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-8">
          <!-- Company Info -->
          <div class="col-span-1 md:col-span-2">
            <div class="flex items-center space-x-3 mb-4">
              <img src="/src/assets/images/icons/oilbuddy_logo.png" alt="OilBuddy Logo" class="w-10 h-10 rounded-xl shadow-lg shadow-emerald-500/25" />
              <span class="text-xl font-bold text-white">OilBuddyAI</span>
            </div>
            <p class="text-slate-400 mb-6 max-w-md">
              Revolutionizing the oil and gas industry with AI-powered solutions for contractors, companies, and professionals.
            </p>
            <div class="flex space-x-4">
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z"/>
                </svg>
              </a>
              <a href="#" class="text-slate-400 hover:text-emerald-400 transition-colors duration-200">
                <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </a>
            </div>
          </div>

          <!-- Quick Links -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Quick Links</h3>
            <ul class="space-y-2">
              <li><router-link to="/" class="text-slate-400 hover:text-white transition-colors duration-200">Home</router-link></li>
              <li><router-link to="/blog" class="text-slate-400 hover:text-white transition-colors duration-200">Blog</router-link></li>
              <li><router-link to="/support" class="text-slate-400 hover:text-white transition-colors duration-200">Support</router-link></li>
              <li><router-link to="/contact" class="text-slate-400 hover:text-white transition-colors duration-200">Contact</router-link></li>
              <li><router-link to="/roadmap" class="text-slate-400 hover:text-white transition-colors duration-200">Roadmap</router-link></li>
            </ul>
          </div>

          <!-- Contact Info -->
          <div>
            <h3 class="text-lg font-semibold text-white mb-4">Contact</h3>
            <ul class="space-y-2">
              <li><a href="mailto:contact@oilbuddy.ai" class="text-slate-400 hover:text-white transition-colors duration-200">contact@oilbuddy.ai</a></li>
              <li><a href="tel:+1-701-419-0006" class="text-slate-400 hover:text-white transition-colors duration-200">+1 (701) 419-0006</a></li>
              <li class="text-slate-400">117 5th AVE SW<br>Minto ND 58701</li>
            </ul>
          </div>
        </div>

        <div class="border-t border-slate-700/50 mt-8 pt-8 text-center">
          <p class="text-slate-400">&copy; 2024 OilBuddyAI. All rights reserved.</p>
        </div>
      </div>
    </footer>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'

// Mobile menu state
const showMobileMenu = ref(false)

// Feature request form state
const isSubmitting = ref(false)
const featureRequest = ref({
  name: '',
  email: '',
  title: '',
  description: '',
  category: ''
})

// Roadmap data - Based on actual OilBuddyAI features
const completedFeatures = ref([
  {
    id: 1,
    title: 'User Authentication & Registration',
    description: 'Secure login/register system with role-based access (contractor, company, admin)',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 2,
    title: 'Contractor Dashboard',
    description: 'Main dashboard with job marketplace, scheduling, and financial overview',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 3,
    title: 'Company Dashboard',
    description: 'Company portal for job posting, crew management, and operations',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 4,
    title: 'Job Marketplace',
    description: 'Job posting, browsing, and application system for contractors',
    category: 'Core',
    completedDate: 'Q1 2024'
  },
  {
    id: 5,
    title: 'Basic AI Messaging',
    description: 'AI-powered chat assistant for general queries and support',
    category: 'AI',
    completedDate: 'Q2 2024'
  },
  {
    id: 6,
    title: 'Payment Processing',
    description: 'Digital payment system with timesheet integration',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 7,
    title: 'Expense Tracking',
    description: 'Mileage, per diem, fuel, and PPE expense management',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 8,
    title: 'Tax Calculation Tools',
    description: 'Quarterly tax estimates and year-end reporting for contractors',
    category: 'Financial',
    completedDate: 'Q2 2024'
  },
  {
    id: 9,
    title: 'Safety & Compliance',
    description: 'Safety incident tracking and compliance reporting system',
    category: 'Safety',
    completedDate: 'Q3 2024'
  },
  {
    id: 10,
    title: 'Scheduling System',
    description: 'Calendar integration with rotation logic (2 weeks on/1 off)',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 11,
    title: 'Timesheets',
    description: 'Digital timesheet submission and approval workflow',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 12,
    title: 'Bank Account Management',
    description: 'Multiple bank account linking and transaction tracking',
    category: 'Financial',
    completedDate: 'Q3 2024'
  },
  {
    id: 13,
    title: 'Profile Management',
    description: 'User profile customization and document upload',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 14,
    title: 'Settings & Preferences',
    description: 'User settings, notifications, and account preferences',
    category: 'Core',
    completedDate: 'Q3 2024'
  },
  {
    id: 15,
    title: 'Knowledge Hub',
    description: 'Industry resources, guides, and educational content',
    category: 'Content',
    completedDate: 'Q4 2024'
  },
  {
    id: 16,
    title: 'Store & Marketplace',
    description: 'Equipment and supplies marketplace for industry professionals',
    category: 'Commerce',
    completedDate: 'Q4 2024'
  },
  {
    id: 17,
    title: 'Support System',
    description: 'Customer support ticketing and help center',
    category: 'Support',
    completedDate: 'Q4 2024'
  },
  {
    id: 18,
    title: 'Admin Panel',
    description: 'Comprehensive admin dashboard for platform management',
    category: 'Admin',
    completedDate: 'Q4 2024'
  }
])

const inProgressFeatures = ref([
  {
    id: 19,
    title: 'Advanced AI Tax Optimization',
    description: 'AI-powered tax optimization and deduction recommendations',
    category: 'AI',
    progress: 85,
    estimatedCompletion: 'Q1 2026'
  },
  {
    id: 20,
    title: 'AI Agentic Framework',
    description: 'Autonomous AI agents for complex task execution and decision making',
    category: 'AI',
    progress: 70,
    estimatedCompletion: 'Q1 2026'
  },
  {
    id: 21,
    title: 'Mobile App Development',
    description: 'Native mobile applications for iOS and Android field operations',
    category: 'Mobile',
    progress: 60,
    estimatedCompletion: 'Q2 2026'
  },
  {
    id: 22,
    title: 'Advanced Analytics Dashboard',
    description: 'Comprehensive analytics with predictive insights and KPI tracking',
    category: 'Analytics',
    progress: 45,
    estimatedCompletion: 'Q2 2026'
  },
  {
    id: 23,
    title: 'Third-party Integrations',
    description: 'API integrations with QuickBooks, Salesforce, and other platforms',
    category: 'Integrations',
    progress: 30,
    estimatedCompletion: 'Q2 2026'
  }
])

const plannedFeatures = ref([
  {
    id: 24,
    title: 'AI-Powered Forecasting',
    description: 'Predictive analytics for project timelines, costs, and resource allocation',
    category: 'AI',
    plannedDate: 'Q3 2025'
  },
  {
    id: 25,
    title: 'Blockchain Smart Contracts',
    description: 'Secure, transparent payment and contract management using blockchain',
    category: 'Blockchain',
    plannedDate: 'Q3 2025'
  },
  {
    id: 26,
    title: 'Advanced Reporting Engine',
    description: 'Custom report builder with data visualization and automated insights',
    category: 'Analytics',
    plannedDate: 'Q4 2025'
  },
  {
    id: 27,
    title: 'Multi-language Support',
    description: 'Internationalization for global markets and multi-lingual users',
    category: 'Core',
    plannedDate: 'Q4 2025'
  },
  {
    id: 28,
    title: 'API Developer Platform',
    description: 'Public API marketplace for third-party developers and integrations',
    category: 'Platform',
    plannedDate: 'Q1 2026'
  }
])

const futureFeatures = ref([
  {
    id: 29,
    title: 'AR/VR Field Support',
    description: 'Augmented reality support for field operations',
    category: 'Emerging Tech',
    priority: 'High'
  },
  {
    id: 30,
    title: 'IoT Device Integration',
    description: 'Real-time data from IoT sensors and equipment',
    category: 'IoT',
    priority: 'Medium'
  },
  {
    id: 31,
    title: 'Machine Learning Optimization',
    description: 'Advanced ML algorithms for process optimization',
    category: 'AI',
    priority: 'High'
  },
  {
    id: 32,
    title: 'Voice Assistant',
    description: 'Voice-controlled interface for hands-free operation',
    category: 'AI',
    priority: 'Medium'
  },
  {
    id: 33,
    title: 'Predictive Maintenance',
    description: 'AI-driven equipment maintenance predictions',
    category: 'IoT',
    priority: 'High'
  }
])

const timeline = ref([
  {
    id: 1,
    title: 'Foundation Launch',
    date: 'Q1 2024',
    description: 'Core platform launch with user authentication, job management, and basic AI features',
    tags: ['Core', 'AI', 'MVP']
  },
  {
    id: 2,
    title: 'Financial Integration',
    date: 'Q2 2024',
    description: 'Payment processing, invoicing, and financial management tools',
    tags: ['Financial', 'Core']
  },
  {
    id: 3,
    title: 'Safety & Compliance',
    date: 'Q3 2024',
    description: 'Safety incident tracking, compliance reporting, and risk management',
    tags: ['Safety', 'Compliance']
  },
  {
    id: 4,
    title: 'Advanced AI',
    date: 'Q4 2024',
    description: 'Sophisticated AI agents and autonomous task execution',
    tags: ['AI', 'Advanced']
  },
  {
    id: 5,
    title: 'Mobile Platform',
    date: 'Q1 2025',
    description: 'Native mobile applications for field operations',
    tags: ['Mobile', 'Core']
  },
  {
    id: 6,
    title: 'Global Expansion',
    date: 'Q2 2025',
    description: 'Multi-language support and international market entry',
    tags: ['Global', 'Platform']
  }
])

// Computed properties
const completedFeaturesCount = computed(() => completedFeatures.value.length)
const inProgressFeaturesCount = computed(() => inProgressFeatures.value.length)
const plannedFeaturesCount = computed(() => plannedFeatures.value.length)
const futureFeaturesCount = computed(() => futureFeatures.value.length)
const totalFeaturesCount = computed(() => 
  completedFeaturesCount.value + inProgressFeaturesCount.value + plannedFeaturesCount.value + futureFeaturesCount.value
)

// AI-powered roadmap management
const roadmapAI = {
  // AI agent for analyzing feature requests and suggesting new features
  analyzeFeatureRequests: async (requests: any[]) => {
    try {
      const response = await fetch('/api/ai/roadmap-analysis', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          featureRequests: requests,
          currentFeatures: completedFeatures.value,
          industryTrends: true
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI analysis failed:', error)
      return null
    }
  },

  // AI agent for suggesting feature priorities
  suggestPriorities: async (features: any[]) => {
    try {
      const response = await fetch('/api/ai/feature-prioritization', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          features: features,
          userFeedback: true,
          marketDemand: true
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI prioritization failed:', error)
      return null
    }
  },

  // AI agent for generating timeline suggestions
  generateTimeline: async (features: any[]) => {
    try {
      const response = await fetch('/api/ai/timeline-generation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          features: features,
          teamCapacity: true,
          dependencies: true
        })
      })
      return await response.json()
    } catch (error) {
      console.error('AI timeline generation failed:', error)
      return null
    }
  }
}

// Methods
const toggleMobileMenu = () => {
  showMobileMenu.value = !showMobileMenu.value
}

const submitFeatureRequest = async () => {
  isSubmitting.value = true
  
  try {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    // Reset form
    Object.assign(featureRequest.value, {
      name: '',
      email: '',
      title: '',
      description: '',
      category: ''
    })
    
    alert('Thank you for your feature request! We\'ll review it and get back to you soon.')
  } catch (error) {
    alert('There was an error submitting your request. Please try again.')
  } finally {
    isSubmitting.value = false
  }
}
</script>

<style scoped>
.btn-primary-modern {
  @apply inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all duration-300 transform hover:scale-105;
}

.btn-outline {
  @apply inline-flex items-center px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-700/50 hover:text-white transition-colors duration-200;
}

.btn-primary {
  @apply inline-flex items-center px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors duration-200;
}
</style>
